/**
 * CreateOrgObjectiveDialog Component
 *
 * Dialog for creating a new organization objective with team assignment
 */

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    Typography,
    Autocomplete,
    Checkbox,
    Chip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { Business, CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import { useOKRDashboardContext } from '../OKRDashboardContext';
import { okrApi } from '../../../services/okrApi';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

export const CreateOrgObjectiveDialog: React.FC = () => {
    const __muiTheme = useTheme();
    const isMobile = useMediaQuery(__muiTheme.breakpoints.down("sm"));
    const { isDarkMode, data, dialogs } = useOKRDashboardContext();
    const { activePeriod, departments, setSuccess, setError, refetchData } = data;
    const { dialogs: dialogState, formState, updateFormField, closeCreateOrgDialog } = dialogs;

    const [selectedDepartments, setSelectedDepartments] = useState<any[]>([]);

    const deptOptions = departments.map((d: any) => ({
        id: d.dept_id ?? d.id,
        name: d.dept_name ?? d.name,
    }));

    const handleCreate = async () => {
        if (!formState.newOrgTitle.trim() || !activePeriod?.id) {
            setError(!formState.newOrgTitle.trim() ? 'Please enter an objective title' : 'No active period selected');
            return;
        }

        try {
            const departmentIds = selectedDepartments.map(d => d.id);
            await okrApi.createOrgObjective(
                activePeriod.id,
                formState.newOrgTitle,
                formState.newOrgDescription,
                departmentIds.length > 0 ? departmentIds : undefined
            );
            setSuccess('Organization objective created successfully');
            setSelectedDepartments([]);
            closeCreateOrgDialog();
            refetchData();
        } catch (err: any) {
            setError(err.message || 'Failed to create organization objective');
        }
    };

    const handleClose = () => {
        setSelectedDepartments([]);
        closeCreateOrgDialog();
    };

    return (
        <Dialog
            open={dialogState.createOrgDialog}
            onClose={handleClose}
            fullScreen={isMobile}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: isDarkMode ? '#1e293b' : '#ffffff',
                    borderRadius: 3,
                    minHeight: '420px',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Business sx={{ color: '#fff', fontSize: 24 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Create Organization Objective
                        </Typography>
                        <Typography variant="caption" sx={{ color: isDarkMode ? '#94a3b8' : '#64748b' }}>
                            For period: {activePeriod?.name || 'No period selected'}
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: '24px !important', pb: 2, overflowY: 'auto' }}>
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <TextField
                        autoFocus
                        label="Objective Title"
                        fullWidth
                        value={formState.newOrgTitle}
                        onChange={(e) => updateFormField('newOrgTitle', e.target.value)}
                        placeholder="e.g., Launch product with 90% accuracy"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <TextField
                        label="Description (Optional)"
                        fullWidth
                        multiline
                        rows={3}
                        value={formState.newOrgDescription}
                        onChange={(e) => updateFormField('newOrgDescription', e.target.value)}
                        placeholder="Describe the objective and its key outcomes..."
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <Autocomplete
                        multiple
                        options={deptOptions}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option.name}
                        value={selectedDepartments}
                        onChange={(_, newValue) => setSelectedDepartments(newValue)}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderOption={(props, option, { selected }) => {
                            const { key, ...rest } = props as any;
                            return (
                                <li key={key} {...rest}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option.name}
                                </li>
                            );
                        }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const { key, ...rest } = getTagProps({ index }) as any;
                                return (
                                    <Chip
                                        key={key}
                                        label={option.name}
                                        size="small"
                                        {...rest}
                                        sx={{
                                            bgcolor: 'rgba(99, 102, 241, 0.15)',
                                            color: '#818cf8',
                                            fontWeight: 600,
                                        }}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Assign to Teams (Optional)"
                                placeholder={selectedDepartments.length === 0 ? "Select teams..." : ""}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, pt: 1 }}>
                <Button
                    onClick={handleClose}
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        fontWeight: 600,
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleCreate}
                    disabled={!formState.newOrgTitle.trim()}
                    sx={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        fontWeight: 600,
                        borderRadius: 2,
                        px: 3,
                    }}
                >
                    Create Objective
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateOrgObjectiveDialog;
